/**
 * Free-fly camera for a found hider.
 *
 * A found player is frozen server-side (`canMove` in round.ts returns false
 * once `found` is set) — their body still exists, is still simulated for
 * gravity, and the predictor keeps producing a `renderPose` for it, but
 * nothing they press moves it. Riding that frozen pose would leave a
 * spectator staring at one fixed point for the rest of the round. This
 * decouples the camera from the predictor entirely once found and flies it
 * under direct input instead.
 *
 * Deliberately not part of the simulation `step` in movement.ts touches: a
 * spectator is not a participant the server needs to agree about, so this
 * never queries `StaticWorld` and never produces anything sent on the wire.
 * `InputSampler.buttons` is read directly rather than the gated frame
 * `main.ts` builds for the network — `canMove` zeroes movement bits before
 * sending because the player's *body* cannot move, which has nothing to say
 * about a camera that was never simulated in the first place.
 */

import { Btn } from "@prop-hunt/shared";

/** Faster than either player speed (rules.ts: HIDER_SPEED is 6) — a
 *  spectator is not racing anyone and slow flight just wastes their time. */
const FLY_SPEED = 10;

export interface SpectatorPose {
  x: number;
  y: number;
  z: number;
}

/**
 * One tick of free flight. A pure function of the current pose and input, so
 * the movement math is testable without a renderer or a DOM.
 *
 * Horizontal movement follows `yaw` the same way `movement.ts`'s wish
 * direction does (yaw 0 faces -Z) so the controls feel identical to normal
 * play. Vertical is a separate axis on Jump/Crouch rather than folded into
 * look pitch — noclip-style, and easier to hold steady than "look up to
 * rise".
 */
export function stepSpectatorCamera(
  pose: SpectatorPose,
  buttons: number,
  yaw: number,
  dtMs: number,
): SpectatorPose {
  const dt = dtMs / 1000;

  let forward = 0;
  let strafe = 0;
  let vertical = 0;
  if (buttons & Btn.Forward) forward += 1;
  if (buttons & Btn.Back) forward -= 1;
  if (buttons & Btn.Right) strafe += 1;
  if (buttons & Btn.Left) strafe -= 1;
  if (buttons & Btn.Jump) vertical += 1;
  if (buttons & Btn.Crouch) vertical -= 1;

  const sy = Math.sin(yaw);
  const cy = Math.cos(yaw);
  let wishX = -sy * forward + cy * strafe;
  let wishZ = -cy * forward - sy * strafe;
  const len = Math.hypot(wishX, wishZ);
  if (len > 0) {
    wishX /= len;
    wishZ /= len;
  }

  return {
    x: pose.x + wishX * FLY_SPEED * dt,
    y: pose.y + vertical * FLY_SPEED * dt,
    z: pose.z + wishZ * FLY_SPEED * dt,
  };
}
